/**
 * @proofroom/schema
 *
 * The shared contract between every Proofroom component:
 *   - the InDesign plugin (exports Snapshots, applies Changes)
 *   - the server (stores Drafts, Changes, Threads, AuditEvents)
 *   - the portal (renders Snapshots, edits Stories, recomposes with the engine)
 *   - the engine (composes Stories into Lines, must agree with InDesign)
 *   - the harness (diffs engine Lines against InDesign Lines)
 *
 * Units: every geometric value is in PostScript POINTS (1/72 in) unless the
 * field name says otherwise. Coordinates are relative to the top-left corner
 * of the SPREAD the item sits on, y growing downward. mm values coming out of
 * InDesign are converted on export: pt = mm * 72 / 25.4.
 *
 * IDs: InDesign object ids are persisted in the .indd file, so `Story.id` and
 * `PageItem.id` are `String(idObject.id)` and stable across sessions.
 * Paragraph ids are minted by the exporter and stored back into the story as a
 * label (see PARA_ID_LABEL) so they survive re-export.
 */
export const SCHEMA_VERSION = 1;
/** InDesign label keys used to persist Proofroom identity inside the .indd */
export const DOC_ID_LABEL = 'proofroom.docId';
export const PARA_ID_LABEL = 'proofroom.paraIds'; // JSON string[] on the Story, one id per paragraph
export const PARA_FP_LABEL = 'proofroom.paraFps'; // JSON string[] beside it: a text fingerprint per paragraph, so ids reconcile by content
export const ITEM_ROLE_LABEL = 'proofroom.role'; // optional: 'placeholder' | 'decoration' | 'table'
export const MM_TO_PT = 72 / 25.4;
export const PT_TO_MM = 25.4 / 72;
// ---------------------------------------------------------------------------
// Email notifications
// ---------------------------------------------------------------------------
/** The one list: the server fans out from it and the portal's preferences screen renders from it. */
export const NOTIFICATION_KINDS = ['draft.published', 'draft.approved', 'change.proposed', 'change.decided', 'thread.created', 'comment.added'];
export const NOTIFICATION_MODES = ['off', 'immediate', 'digest'];
/** Sentence case, for the preferences screen and nothing else; emails carry their own summary. */
export const NOTIFICATION_KIND_LABELS = {
    'draft.published': 'A new draft is published',
    'draft.approved': 'A draft is approved as final',
    'change.proposed': 'A change is sent to the designer',
    'change.decided': 'A change you proposed is decided',
    'thread.created': 'A comment thread is started',
    'comment.added': 'Someone replies on a thread you are in',
};
// ---------------------------------------------------------------------------
// Plugin <-> server protocol (WebSocket, JSON frames)
// ---------------------------------------------------------------------------
export const PLUGIN_WS_PATH = '/ws/plugin';
export const YJS_WS_PATH = '/ws/yjs';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
export function cmykToHex(c, m, y, k) {
    const r = 255 * (1 - c / 100) * (1 - k / 100);
    const g = 255 * (1 - m / 100) * (1 - k / 100);
    const b = 255 * (1 - y / 100) * (1 - k / 100);
    return rgbToHex(r, g, b);
}
export function rgbToHex(r, g, b) {
    const h = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${h(r)}${h(g)}${h(b)}`;
}
export function tintHex(hex, tint) {
    if (!hex)
        return '';
    const t = Math.max(0, Math.min(100, tint)) / 100;
    const n = parseInt(hex.slice(1), 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    return rgbToHex(255 - (255 - r) * t, 255 - (255 - g) * t, 255 - (255 - b) * t);
}
export function resolveLeading(a) {
    return a.leading === 'auto' ? (a.pointSize * a.autoLeading) / 100 : a.leading;
}
/** Column x-ranges (spread pt) for a frame, after insets */
export function columnRects(f) {
    const [t, l, b, r] = f.insets;
    const x0 = f.bounds.x + l, y0 = f.bounds.y + t;
    const innerW = f.bounds.w - l - r, innerH = f.bounds.h - t - b;
    const n = Math.max(1, f.columns.count);
    const g = f.columns.gutter;
    const out = [];
    if (f.columns.widths && f.columns.widths.length === n) {
        let x = x0;
        for (let i = 0; i < n; i++) {
            out.push({ x, y: y0, w: f.columns.widths[i], h: innerH });
            x += f.columns.widths[i] + g;
        }
        return out;
    }
    const cw = (innerW - g * (n - 1)) / n;
    for (let i = 0; i < n; i++)
        out.push({ x: x0 + i * (cw + g), y: y0, w: cw, h: innerH });
    return out;
}
export { faceEmphasis, referencedFontIds, resolveFace } from './fonts.js';
//# sourceMappingURL=index.js.map